import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { usePageCacheState } from '../contexts/PageCacheContext';
import {
  Mail,
  FileText,
  Activity,
  Upload,
  MessageSquare
} from 'lucide-react';

import { DashboardStats as IDashboardStats, MailTrackingInfo, StatCardData, QuickActionData } from '../components/dashboard/types';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardStats from '../components/dashboard/DashboardStats';
import QuickActions from '../components/dashboard/QuickActions';
import MailTrackingSummary from '../components/dashboard/MailTrackingSummary';

export default function Dashboard() {
  const { user, refreshAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = usePageCacheState<IDashboardStats>('dashboard.stats', {
    emailAccounts: 0,
    templates: 0,
    recentEmails: 0,
    activeTemplates: 0,
    keywords: 0,
  });
  const [mailTracking, setMailTracking] = usePageCacheState<MailTrackingInfo[]>(
    'dashboard.mailTracking',
    []
  );
  const [loading, setLoading] = usePageCacheState<boolean>(
    'dashboard.loading',
    true
  );
  const hasFetchedRef = useRef(false);

  // Payment success/failed query parameter kontrolü
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success' || paymentStatus === 'failed') {
      // User state'i refresh et (subscription plan güncellenecek)
      refreshAuth();
      // Query parametresini temizle
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshAuth]);

  const loadStats = async () => {
    try {
      const hasCachedState =
        stats.emailAccounts > 0 ||
        stats.templates > 0 ||
        stats.activeTemplates > 0 ||
        stats.keywords > 0 ||
        mailTracking.length > 0;

      if (!hasCachedState) {
        setLoading(true);
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setStats({
          emailAccounts: 0,
          templates: 0,
          recentEmails: 0,
          activeTemplates: 0,
          keywords: 0,
        });
        setMailTracking([]);
        setLoading(false);
        return;
      }

      const requestConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const [emailResult, templatesResult, keywordsResult] = await Promise.allSettled([
        apiClient.get('/email-accounts', requestConfig),
        apiClient.get('/templates', requestConfig),
        apiClient.get('/keywords', requestConfig),
      ]);

      const emailData =
        emailResult.status === 'fulfilled' && emailResult.value.data.success
          ? emailResult.value.data.data || []
          : [];
      const templatesData =
        templatesResult.status === 'fulfilled' && templatesResult.value.data.success
          ? templatesResult.value.data.data || []
          : [];
      const keywordData =
        keywordsResult.status === 'fulfilled' && keywordsResult.value.data.success
          ? keywordsResult.value.data.data || []
          : [];

      const lastWeek = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recentlySyncedAccounts = emailData.filter((account: any) => {
        if (!account.last_sync) return false;
        return new Date(account.last_sync).getTime() >= lastWeek;
      }).length;

      const newStats: IDashboardStats = {
        emailAccounts: emailData.length,
        templates: templatesData.length,
        recentEmails: recentlySyncedAccounts,
        activeTemplates: templatesData.filter((t: any) => t.is_active).length,
        keywords: keywordData.length,
      };

      const trackingData: MailTrackingInfo[] = keywordData.map((keyword: any) => ({
        id: keyword.id.toString(),
        name: keyword.name,
        pattern: keyword.pattern || keyword.regex_pattern || '',
        description: keyword.description,
        updatedAt: keyword.updated_at,
        createdAt: keyword.created_at,
      }));

      setMailTracking(trackingData);
      setStats(newStats);
    } catch (error) {
      // İstatistikler yüklenirken hata oluştu
    } finally {
      setLoading(false);
    }
  };

  // Load stats on component mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboardCards: StatCardData[] = [
    {
      name: 'Email Hesapları',
      value: stats.emailAccounts.toString(),
      icon: Mail,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/email-accounts',
      change: 'Canlı',
      changeType: 'positive' as const
    },
    {
      name: 'Aktif Şablonlar',
      value: stats.activeTemplates.toString(),
      icon: FileText,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/templates',
      change: 'Canlı',
      changeType: 'positive' as const
    },
    {
      name: 'Son 7 Gün Senkronize Hesap',
      value: stats.recentEmails.toString(),
      icon: Activity,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      link: '/mail-tracking',
      change: 'Canlı',
      changeType: 'positive' as const
    },
    {
      name: 'Toplam Şablon',
      value: stats.templates.toString(),
      icon: Upload,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600',
      link: '/templates',
      change: 'Canlı',
      changeType: 'positive' as const
    }
  ];

  const quickActions: QuickActionData[] = [
    {
      title: 'Mail Takip Başlat',
      description: 'Email hesaplarınızı tarayarak yeni mailleri takip edin',
      icon: Activity,
      link: '/mail-tracking',
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Yeni Şablon Yükle',
      description: 'DOCX şablonlarınızı yükleyip otomatik form oluşturun',
      icon: Upload,
      link: '/templates',
      color: 'bg-green-50 hover:bg-green-100 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Geri Bildirim Gönder',
      description: 'Görüşlerinizi bizimle paylaşın ve deneyiminizi iyileştirin',
      icon: MessageSquare,
      link: '/feedback',
      color: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      iconColor: 'text-purple-600'
    },
  ];

  const userName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.email || 'Kullanıcı';

  return (
    <div className="space-y-8">
      <DashboardHeader userName={userName} />

      <DashboardStats loading={loading} cards={dashboardCards} />

      <QuickActions actions={quickActions} />

      <MailTrackingSummary
        mailTracking={mailTracking}
        keywordCount={stats.keywords}
      />
    </div>
  );
}
